import { useRef, type ChangeEvent } from 'react';
import {
  Flex,
  Text,
  Link,
  Tag,
  FormLabel,
  Collapse,
  Box,
  Icon,
  Alert,
  AlertIcon,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import { setAppleEnhancedDesignType } from '../../../store/redux/slices/template.slice';
import { clearNamespaceErrors } from '../../../store/redux/slices/templateErrors.slice';
import InfoBanner from '../../common/InfoBanner';
import BuilderSwitch from './BuilderSwitch';
import SectionWrapper from './SectionWrapper';
import ImageUploadField from './ImageUploadField';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { useEnhancedDesignForTemplate } from './hooks/useEnhancedDesignForTemplate';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { ENHANCED_EVENT_TICKET_IMAGE_LABELS } from './utils/enhancedEventTicketLabels';
import {
  APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE,
  APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE,
  ENHANCED_GENERIC_SECTION_ID,
  ENHANCED_EVENT_TICKET_SECTION_ID,
} from './utils/appleEnhancedDesignConstants';
import { useDemoPass } from './DemoPassContext';
import { POSTER_DEMO_IMAGES } from './previews/apple/demoPass/posterDemoAssets';
import PremiumFeatureTag from './PremiumFeatureTag';
import PlanUpgradeButton, { useIsLegacyBilling } from './PlanUpgradeButton';
import { designRequiresTemplateNfc } from './utils/enhancedDesignEligibility';
import { EnhancedDesignType } from '../../../store/types/template';
import DisableEnhancedDesignModal from './modals/DisableEnhancedDesignModal';
import { CustomInfoIcon } from '../../../theme/icons/CustomInfoIcon';

interface Props {
  design: EnhancedDesignType;
  onNavigateToNfc?: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const GUIDE_URL = 'https://docs.passentry.com';

const DESIGN_CONFIG: Record<
  EnhancedDesignType,
  {
    sectionId: string;
    title: string;
    enableFieldId: string;
    focusField: string;
    tooltip: string;
  }
> = {
  generic: {
    sectionId: ENHANCED_GENERIC_SECTION_ID,
    title: 'Enhanced pass',
    enableFieldId: 'enhanced-generic-enabled',
    focusField: 'appleEnhancedDesign.generic.enabled',
    tooltip:
      'A richer Apple Wallet pass with full-bleed artwork and quick action links.',
  },
  eventTicket: {
    sectionId: ENHANCED_EVENT_TICKET_SECTION_ID,
    title: 'Enhanced ticket',
    enableFieldId: 'enhanced-event-ticket-enabled',
    focusField: 'appleEnhancedDesign.eventTicket.enabled',
    tooltip:
      'A richer Apple Wallet event ticket with full-bleed artwork, an event guide and quick action links — event details are added when issuing each pass.',
  },
};

const Ios27Tag = () => (
  <Tag
    bg="rgba(0, 122, 255, 0.25)"
    color="#007aff"
    borderRadius="3px"
    px={1}
    py={1}
    fontSize="12px"
    fontWeight="500"
    lineHeight="1.3"
  >
    iOS 27+
  </Tag>
);

const SUPPORT_EMAIL = 'support@passentry.com';

const SupportLink = () => (
  <Link
    href={`mailto:${SUPPORT_EMAIL}`}
    textDecoration="underline"
    color="inherit"
  >
    {SUPPORT_EMAIL}
  </Link>
);

const buildLockedMessage = (
  missingPassBinding: boolean,
  isLegacyBilling: boolean
) => (
  <>
    Apple NFC access is restricted. Contact <SupportLink /> to request access.
    {missingPassBinding &&
      ` Pass binding also isn't in your plan, so you'll need to ${
        isLegacyBilling ? 'contact sales' : 'upgrade your plan'
      } to publish.`}
  </>
);

const getPremiumTooltip = (
  missingNfcEntitlement: boolean,
  missingPassBinding: boolean,
  isLegacyBilling: boolean
): string | undefined => {
  if (missingNfcEntitlement) {
    return 'Needs Apple NFC. Contact support to request access.';
  }
  if (missingPassBinding) {
    return `Not in your plan - try it in a draft, ${
      isLegacyBilling ? 'contact sales' : 'upgrade'
    } to publish.`;
  }
  return undefined;
};

const EnhancedDesignFeature = ({
  design,
  onNavigateToNfc,
  isOpen = true,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const { isDemoPass } = useDemoPass();
  const hook = useEnhancedDesignForTemplate();
  const isEventTicket = design === 'eventTicket';
  const { sectionId, title, enableFieldId, focusField, tooltip } =
    DESIGN_CONFIG[design];

  const errors = useAppSelector((s) => s.templateErrors);
  const passesCount = useAppSelector((s) => s.template._passesCount);
  const typeOnLoad = useAppSelector(
    (s) => s.template._appleEnhancedDesignOnLoad.type
  );
  const disableModal = useDisclosure();

  const enabledRef = useRef<HTMLDivElement>(null);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      [focusField]: enabledRef,
    },
  });

  const { validateField, validateSection, handleFocus } =
    useTemplateFieldValidation(APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE);

  const designDisclosure = useDisclosure({ defaultIsOpen: true });

  const isChecked = hook.storedDesign === design;
  const needsTemplateNfc =
    designRequiresTemplateNfc(design) && !hook.templateNfcEnabled;
  const isLegacyBilling = useIsLegacyBilling();
  const locked = hook.isLocked(design);
  const showPassBindingNote =
    isEventTicket && !locked && hook.missingPassBinding;

  const showRestoredBanner =
    typeOnLoad === null && isChecked && passesCount > 0;

  const applyEnabled = (checked: boolean) => {
    dispatch(setAppleEnhancedDesignType(checked ? design : null));
    dispatch(clearNamespaceErrors(APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE));
    dispatch(
      clearNamespaceErrors(APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE)
    );
  };

  const handleToggleEnabled = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const revertsIssuedPasses =
      !checked && passesCount > 0 && typeOnLoad === design;
    if (revertsIssuedPasses) {
      disableModal.onOpen();
      return;
    }
    applyEnabled(checked);
  };

  if (!hook.allowedDesigns.includes(design) || !hook.available) {
    return null;
  }

  return (
    <SectionWrapper
      id={sectionId}
      locked={locked}
      lockedMessage={
        isEventTicket
          ? buildLockedMessage(hook.missingPassBinding, isLegacyBilling)
          : undefined
      }
      showUpgradeCta={isEventTicket && hook.missingPassBinding}
      openOnLoad={isOpen}
      onClose={onClose}
      autoOpenOnceWhen={isFieldBeingEdited}
      title={title}
      tooltip={tooltip}
      titleBadge={
        isEventTicket ? (
          <PremiumFeatureTag
            tooltip={getPremiumTooltip(
              hook.missingNfcEntitlement,
              hook.missingPassBinding,
              isLegacyBilling
            )}
          />
        ) : (
          <Ios27Tag />
        )
      }
      subtitle={
        isEventTicket ? (
          <Text>
            Available on iOS 18 and later. Need help?{' '}
            <Link
              variant="grey"
              textDecoration="underline"
              href={GUIDE_URL}
              target="_blank"
            >
              Read the guide
            </Link>
          </Text>
        ) : (
          <Text>
            Enable to create an enhanced version of this pass for iOS 27.{' '}
            <Link
              variant="grey"
              textDecoration="underline"
              href={GUIDE_URL}
              target="_blank"
            >
              Learn more →
            </Link>
          </Text>
        )
      }
    >
      <Flex direction="column" gap={8}>
        {showPassBindingNote && (
          <Box>
            <Text fontSize="16px" color="grey" mb={6}>
              Pass binding isn't in your plan. You can save this as a draft, but
              you'll need to{' '}
              {isLegacyBilling ? 'contact sales' : 'upgrade your plan'} to
              publish it.
            </Text>
            <PlanUpgradeButton />
          </Box>
        )}
        {needsTemplateNfc && (
          <Alert
            status="warning"
            bg="warningOrangeLight"
            borderRadius="8px"
            fontSize="14px"
          >
            <AlertIcon color="warningOrange" />
            <Box>
              NFC is required for enhanced ticket.{' '}
              <Link
                onClick={onNavigateToNfc}
                textDecoration="underline"
                cursor="pointer"
              >
                Enable NFC
              </Link>{' '}
              to use.
            </Box>
          </Alert>
        )}

        <FieldEditIndicator
          fieldRef={enabledRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="10px"
        >
          <Flex
            align="center"
            justify="space-between"
            onClick={() => handleFocus(focusField)}
          >
            <FormLabel
              htmlFor={enableFieldId}
              variant="templateBuilderLabel"
              mb={0}
            >
              Enable
            </FormLabel>
            <BuilderSwitch
              id={enableFieldId}
              isChecked={isChecked}
              isDisabled={needsTemplateNfc}
              onChange={handleToggleEnabled}
            />
          </Flex>
        </FieldEditIndicator>

        {showRestoredBanner && (
          <InfoBanner
            color="#009BD6"
            bg="rgba(0, 155, 214, 0.2)"
            icon={<CustomInfoIcon color="#009BD6" boxSize="22px" flexShrink={0} />}
            hasLeftBorder={false}
            borderRadius="5px"
            mt={0}
            py="10px"
          >
            <Text fontSize="14px" lineHeight="1.4" color="rgba(0, 0, 0, 0.8)">
              Enhanced pass will be restored for all issued passes when you save
              this template.
            </Text>
          </InfoBanner>
        )}

        {isEventTicket && (
          <Box>
            <Flex
              align="center"
              justify="space-between"
              cursor="pointer"
              onClick={designDisclosure.onToggle}
              mb={designDisclosure.isOpen ? 4 : 0}
            >
              <Text fontSize="16px" fontWeight="600">
                Design
              </Text>
              <Icon
                as={designDisclosure.isOpen ? ChevronUpIcon : ChevronDownIcon}
                boxSize="22px"
              />
            </Flex>
            <Collapse in={designDisclosure.isOpen}>
              <Flex direction="column" gap={8}>
                {(['secondaryLogo', 'venueMap'] as const).map((field) => (
                  <ImageUploadField
                    key={field}
                    source="enhancedEventTicket"
                    field={field}
                    label={ENHANCED_EVENT_TICKET_IMAGE_LABELS[field]}
                    isDisabled={needsTemplateNfc || !isChecked || isDemoPass}
                    demoPreview={
                      isDemoPass ? POSTER_DEMO_IMAGES[field] : undefined
                    }
                    errors={errors}
                    validateField={validateField}
                    validateSection={validateSection}
                  />
                ))}
              </Flex>
            </Collapse>
          </Box>
        )}
      </Flex>
      <DisableEnhancedDesignModal
        isOpen={disableModal.isOpen}
        onClose={disableModal.onClose}
        onConfirm={() => applyEnabled(false)}
        passesCount={passesCount}
      />
    </SectionWrapper>
  );
};

export default EnhancedDesignFeature;
